import { AppName } from "sl-api-connector";
import { AvailableRetailer } from "src/types/store/storeTypes";
import { getAppName } from "src/utils/app";
import { useAppSelector } from "src/utils/Hooks/typedHooks";

// Convert any id-like value to a string; return the string representation or null if invalid
const toStringId = (value: string | number): string | null => {
  if (value === null || value === undefined) return null;
  const stringValue = String(value);
  return stringValue.trim() !== '' ? stringValue : null;
};

// TODO: need to check if categories are available for Atlas
export const useFormCategories = () => {
  const categories = useAppSelector((state) => state.categories);

  // IMPORTANT: id=0 is "All Categories"
  const formCategories = categories
    .filter((category) => category.id !== 0) // 0 is the all category
    .map((category) => {
      const id = toStringId(category.id);
      return {
        id,
        name: category.name ?? 'Unnamed Category',
      };
    })
    .filter((category) => category.id !== null) as Array<{ id: string; name: string }>;

  return formCategories;
};

export const useFormSubcategories = () => {
  const subcategories = useAppSelector((state) => state.subCategories);

  const formSubcategories = subcategories
    .map((subcategory) => {
      const id = toStringId(subcategory.subCategoryId);
      return {
        id,
        // @ts-ignore entity.d.ts is not updated with subCategoryName
        name: subcategory.subCategoryName ?? 'Unnamed Subcategory',
      };
    })
    .filter((subcategory) => subcategory.id !== null) as Array<{ id: string; name: string }>;

  return formSubcategories;
};

const getRetailerName = (retailerId: number, allRetailersMetadata: AvailableRetailer[]): string => {
  const retailer = allRetailersMetadata.find((r) => r.id === String(retailerId));
  return retailer?.name ?? 'Unnamed Retailer';
};

export const useFormRetailers = () => {
  const appName = getAppName();
  const allRetailersMetadata = useAppSelector((state) => state.retailer.availableRetailers);
  // @ts-ignore
  const beaconRetailerIds = useAppSelector((state) => state.entityService.mainEntity?.retailerIds) as number[] || [];
  const atlasRetailerIds = useAppSelector((state) => state.user.config.allRetailerIds) as number[] || [];
  if (appName === AppName.Atlas) {
    return atlasRetailerIds
      .map((retailerId) => {
        return {
          id: toStringId(retailerId),
          name: getRetailerName(retailerId, allRetailersMetadata),
        };
      })
      .filter((retailer) => retailer.id !== null) as Array<{ id: string; name: string }>;
  }

  return beaconRetailerIds
    .map((retailerId) => {
      return {
        id: toStringId(retailerId),
        name: getRetailerName(retailerId, allRetailersMetadata),
      };
    })
    .filter((retailer) => retailer.id !== null) as Array<{ id: string; name: string }>;
};

export const useFormBrands = () => {
  const appName = getAppName();
  const brands = useAppSelector(
    (state) =>
      state.brandsFollowing as Array<{
        id?: number;
        brandId?: number;
        name?: string;
        brandName?: string;
        displayName?: string;
      }>
  );

  // in atlas, we need to allow all brands, not just brandsFollowing
  // temporarily we will return empty array, so arbitrary brands are allowed
  if (appName === AppName.Atlas) {
    return [];
  }

  return brands
    .filter((brand) => brand.id !== 0) // 0 is the all brand, we ignore it
    .map((brand) => {
      const id = toStringId(brand.id);
      const name = brand.brandName ?? brand.displayName ?? brand.name ?? 'Unnamed Brand';
      return { id, name };
    })
    .filter((brand) => brand.id !== null && brand.id !== '0') as Array<{ id: string; name: string }>;
};


export const useFormSegments = () => {
  const segments = useAppSelector((state) => state.segments);

  const allSegments = [...segments.mySegments, ...segments.teamSegments];

  const seenIds = new Set<string>();
  return allSegments
    .map((segment) => {
      const id = segment.id;
      const name = segment.displayName ?? 'Unnamed Segment';
      return { id, name };
    })
    .filter((segment) => {
      if (segment.id == null) return false;
      if (seenIds.has(segment.id)) return false;
      seenIds.add(segment.id);
      return true;
    });
};

export const useFormBusinessUnits = () => {
  const businessUnits = useAppSelector((state) => state.segments.businessUnits);

  const seenIds = new Set<string>();
  return businessUnits
    .map((businessUnit) => {
      const id = businessUnit.id;
      const name = businessUnit.displayName ?? 'Unnamed Business Unit';
      return { id, name };
    })
    .filter((businessUnit) => {
      if (businessUnit.id == null) return false;
      if (seenIds.has(businessUnit.id)) return false;
      seenIds.add(businessUnit.id);
      return true;
    });
};

/**
 * Campaigns are not available in Atlas
 * Some account could have 1000 campaigns. It is feasible with a dropdown to show all campaigns.
 */
export const useFormCampaigns = () => {
  const appName = getAppName();
  const campaigns = useAppSelector((state) => state.adCampaigns);

  if (appName === AppName.Atlas) {
    return [];
  }

  return campaigns.map((campaign) => {
    return {
      id: campaign.campaignId,
      name: campaign.campaignName ?? 'Unnamed Campaign',
    };
  });
};
